/**
 * The house mark — who made these games.
 *
 * Every game in the arcade is a different world with a different palette, a
 * different font and a different set of rules; the one thing they share is the
 * studio behind them. That signature is the only piece of art that is allowed to
 * appear, unchanged, inside all ten of them.
 *
 * The artwork is a full studio lockup: a crimson gamepad mark, "GAME STUDIO"
 * set in a squared display face, and the founder's signature on a "BY:" line.
 *
 * IT IS IN COLOUR, AND THAT CHANGED HOW IT IS USED. An earlier version of this
 * mark was a single white silhouette, which could be poured into any colour with
 * a CSS `mask-image` and tinted per game. This one carries its own palette —
 * #E6344A against white — so it is now drawn as ARTWORK rather than as a
 * stencil: an `<img>` in the badge, and real sampled colour in the 3D sequence.
 * The per-game accent moved to the things around it (the glow, the rings, the
 * loading bar), where it can identify the game without repainting the brand.
 *
 * These constants carry no imports on purpose. Both a server layout and a
 * client-side WebGL module read them, and `brand.ts` (which reaches into the
 * game catalogue) must never be pulled into a browser bundle by accident.
 */

/** The studio, spelled out for screen readers and `alt` text. */
export const PROVIDER_NAME = "Ruchira Edirisinghe";

/** Sits above the mark on the title sequence. */
export const PROVIDER_EYEBROW = "Game Provider";

/** Sits beside the mark in the corner of every game. */
export const PROVIDER_POWERED_BY = "Powered by";

/** The wordmark itself, served from /public. */
export const WORDMARK_SRC = "/assets/game_provider_logo.svg";

/** Its intrinsic viewBox, so callers can reserve the right box without loading it. */
export const WORDMARK_W = 935;
export const WORDMARK_H = 550;
export const WORDMARK_ASPECT = WORDMARK_W / WORDMARK_H;

/** The hub's own accent, and the fallback for anything with no game behind it. */
export const DEFAULT_ACCENT = "#8b7cff";

/**
 * How long the title sequence may hold the screen.
 *
 * THE FLOOR IS NOT A GUESS — IT IS THE ANIMATION'S OWN LENGTH PLUS A BEAT. The
 * mark finishes writing itself at ~1.55s and the specular sweep clears it at
 * ~2.45s, so the floor sits ~850ms past the last beat. That gap is the point —
 * it is the only stretch where the mark is simply THERE, solid and still, which
 * is the frame a logo is actually remembered from. An early cut of this held for
 * 2.4s against a 2.4s animation, which meant the completed signature existed for
 * roughly fifty milliseconds: all of the cost of a title sequence, none of the
 * payoff.
 *
 * The floor came DOWN from 4.6s when the write was compressed (see `splash.ts`).
 * The beat at the end is preserved; what went was the time spent watching the
 * logo not exist yet.
 *
 * The ceiling is a tax cap: a splash is paid on every single launch, and past
 * about five seconds a player is waiting rather than watching. Between the two,
 * the real `load` event decides.
 */
export const INTRO_MIN_MS = 3300;
export const INTRO_MAX_MS = 5200;

/**
 * THE EXIT IS A DIP TO BLACK, NOT A CROSS-FADE.
 *
 * Two beats. The screen goes to black over `INTRO_DIP_MS`, then the black
 * lifts off the game over `INTRO_LIFT_MS`.
 *
 * A cross-fade puts the title sequence and the game's own first frame on screen
 * at the same time, half-transparent, both fighting for the same pixels — and
 * every one of these games opens on a different palette, so there is no pairing
 * where that looks deliberate. Going through black separates them: the splash
 * ends, then the game begins. It is the oldest cut in film for the same reason.
 *
 * The dip is quicker than the lift on purpose. Leaving feels like a decision and
 * should be brisk; arriving wants a moment to let the eye adjust to the game's
 * own light.
 */
export const INTRO_DIP_MS = 420;
export const INTRO_LIFT_MS = 560;

/** Reduced-motion players get the mark held still, and for much less time. */
export const INTRO_STILL_MS = 1800;
